import {
  fmtNum,
  roDate,
  todayIso,
  toConfirm,
  siteCanChange,
  type Delivery,
  type SiteOrder,
  t,
} from '@procumi/shared';
import { useMemo, useState } from 'react';
import { askRemove, DeliveryDialog, DeliveryItem, type DeliveryPort } from '../components/Deliveries';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { currentUser } from '../lib/me';

const SITE_KEY = 'procumi.receiving.site';

const remembered = (): string => {
  try {
    return localStorage.getItem(SITE_KEY) ?? '';
  } catch {
    return '';
  }
};

function sitePort(orderId: string): DeliveryPort<SiteOrder> {
  return {
    create: (body) => api.siteCreateDelivery(orderId, body),
    update: (id, body) => api.siteUpdateDelivery(orderId, id, body),
    remove: (id) => api.siteDeleteDelivery(orderId, id),
    upload: (id, file) => api.siteUploadDeliveryFile(orderId, id, file),
    removeFile: (id, fileId) => api.siteDeleteDeliveryFile(orderId, id, fileId),
    fileUrl: (id, fileId) => api.siteDeliveryFileUrl(orderId, id, fileId),
    deliveriesOf: (o) => o.deliveries,
  };
}

/** Rândul de sub aviz, din ochii omului de pe șantier. */
function status(d: Delivery, me: string): string {
  const who = d.createdBy?.id === me ? 'tine' : (d.createdBy?.name ?? t('Achiziții'));
  if (toConfirm(d)) return t('Trecută de {0} · așteaptă confirmarea Achizițiilor', who);
  if (d.confirmedBy && d.createdBy?.id !== d.confirmedBy.id) {
    return t('Trecută de {0} · confirmată de {1}', who, d.confirmedBy.name);
  }
  return t('Trecută de {0}', who);
}

/**
 * „Livrări”, în contul de șantier: comenzile la care mai vine marfă, fără
 * prețuri. Omul are avizul în mână, deci el trece livrarea, cu poza lui;
 * Achizițiile o confirmă după. Până atunci, și-o poate schimba sau șterge.
 */
export function ReceivingPage({ toast }: { toast: Toast }) {
  const { data, setData, error, loading, reload } = useAsync(() => api.receiving(), [], 'receiving');
  const me = currentUser().id;
  const [site, setSiteState] = useState(remembered);
  /** comanda și livrarea din dialog; `delivery` null = livrare nouă */
  const [editing, setEditing] = useState<{ order: SiteOrder; delivery: Delivery | null } | null>(null);
  const today = todayIso();
  const all = useMemo(() => data?.orders ?? [], [data]);
  const sites = useMemo(
    () =>
      [...new Map(all.map((o) => [o.site.id, o.site.name])).entries()].sort((a, b) =>
        a[1].localeCompare(b[1], 'ro'),
      ),
    [all],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} onRetry={reload} />;

  // Un șantier ales odată, care între timp s-a închis, nu mai filtrează nimic.
  const chosen = sites.some(([id]) => id === site) ? site : '';
  const setSite = (id: string) => {
    setSiteState(id);
    try {
      localStorage.setItem(SITE_KEY, id);
    } catch {
      /* fără memorie în browser: alegerea ține doar acum */
    }
  };
  const orders = all.filter((o) => !chosen || o.site.id === chosen);

  const replace = (o: SiteOrder) =>
    setData((prev) => (prev ? { orders: prev.orders.map((x) => (x.id === o.id ? o : x)) } : prev));

  const remove = async (o: SiteOrder, d: Delivery) => {
    if (!(await askRemove(d))) return;
    try {
      replace(await api.siteDeleteDelivery(o.id, d.id));
      toast(t('Livrarea a fost ștearsă'));
    } catch (e) {
      toast(errMsg(e));
    }
  };

  return (
    <>
      <div className="head">
        <div>
          <h1>{t('Livrări')}</h1>
          <p>
            {t(
              'Ce s-a comandat pentru șantier și când se livrează. La fiecare livrare, adaugă avizul cu o poză: Achizițiile îl confirmă după.',
            )}
          </p>
        </div>
      </div>

      {sites.length > 1 ? (
        <div className="list-tools">
          <select
            className="inp"
            value={chosen}
            aria-label={t('Șantier')}
            onChange={(e) => setSite(e.target.value)}
          >
            <option value="">{t('Toate șantierele')}</option>
            {sites.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {orders.length ? (
        orders.map((o) => {
          const port = sitePort(o.id);
          const came = o.deliveries.some((d) => d.kind === 'in');
          const late = !came && !!o.deliveryDate && o.deliveryDate < today;
          return (
            <section key={o.id} className="sheet panel deliveries rcv-card">
              <header className="rcv-h">
                <div className="rcv-t">
                  <h2>{o.supplierName}</h2>
                  <p>
                    {o.title}
                    {o.kind === 'supplement' ? t(' · suplimentare') : ''} ·{' '}
                    <span className="nowrap">{t('comanda {0}', o.number)}</span>
                    {sites.length > 1 && !chosen ? ` · ${o.site.name}` : ''}
                  </p>
                </div>
                <div className={`rcv-when${late ? ' late' : ''}`}>
                  {o.deliveryDate ? (
                    <>
                      <small>{late ? t('trebuia pe') : came ? t('dorită pe') : t('vine pe')}</small>
                      <b>{roDate(o.deliveryDate)}</b>
                    </>
                  ) : (
                    <small>{t('fără dată de livrare')}</small>
                  )}
                </div>
              </header>

              <ul className="rcv-lines" aria-label={t('Ce s-a comandat')}>
                {o.lines.map((l, i) => (
                  <li key={i}>
                    <span>{l.desc}</span>
                    <b>
                      {fmtNum(l.qty)} {l.unit}
                      {l.rent ? <small> {t('· chirie')}</small> : null}
                    </b>
                  </li>
                ))}
              </ul>
              {o.deliveryAddress ? (
                <p className="rcv-addr">{t('Livrare la: {0}', o.deliveryAddress)}</p>
              ) : null}

              {o.deliveries.length ? (
                <ul className="dlv-list">
                  {o.deliveries.map((d) => (
                    <DeliveryItem
                      key={d.id}
                      d={d}
                      fileUrl={(f) => port.fileUrl(d.id, f)}
                      status={status(d, me)}
                    >
                      {siteCanChange(d, me) ? (
                        <>
                          <button className="btn small" onClick={() => setEditing({ order: o, delivery: d })}>
                            {t('Modifică')}
                          </button>
                          <button className="btn small" onClick={() => void remove(o, d)}>
                            {t('Șterge')}
                          </button>
                        </>
                      ) : null}
                    </DeliveryItem>
                  ))}
                </ul>
              ) : null}

              <div className="rcv-f">
                <button className="btn primary" onClick={() => setEditing({ order: o, delivery: null })}>
                  {came ? t('Adaugă încă o livrare') : t('Adaugă livrarea')}
                </button>
              </div>
            </section>
          );
        })
      ) : (
        <div className="sheet rq-empty">
          <p>
            {all.length
              ? t('Nicio comandă pe șantierul ales. Alege „Toate șantierele”.')
              : t(
                  'Nu așteptăm nimic acum. Când Achizițiile fac o comandă pentru șantier, apare aici, cu ce vine și când.',
                )}
          </p>
        </div>
      )}

      {editing ? (
        <DeliveryDialog
          port={sitePort(editing.order.id)}
          existing={editing.order.deliveries}
          orderNumber={editing.order.number}
          supplierName={editing.order.supplierName}
          delivery={editing.delivery}
          rental={editing.order.rental}
          note={t('Achizițiile o confirmă după; până atunci o poți schimba.')}
          toast={toast}
          onUpdated={replace}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}
